<template>
  <UButton
    :block="block"
    :disabled="disabled"
    :href="href"
    :label="label"
    :loading="loading"
    :padded="padded"
    :size="size"
    :target="target"
    :to="to"
    :truncate="truncate"
    :type="to || href ? undefined : type"
    :variant="themeVariant"
    :ui="uiStyles"
    v-bind="{ ...(color ? { color } : {}), ...$attrs }"
  >
    <template v-if="$slots.leading" #leading>
      <slot name="leading" />
    </template>
    <slot />
    <template v-if="$slots.trailing" #trailing>
      <slot name="trailing" />
    </template>
  </UButton>
</template>

<script setup>
  defineOptions({ inheritAttrs: false })

  const styles = {
    favoritely: {
      primary: {
        variant: {
          solid:
            "bg-pink-600 text-white text-center " +
            "dark:bg-pink-500 dark:text-white " +
            "hover:text-white hover:bg-pink-800 dark:hover:bg-pink-600 " +
            "disabled:bg-pink-600 disabled:text-white dark:disabled:bg-pink-500 dark:disabled:text-white",
        },
      },
      secondary: {
        variant: {
          solid:
            "bg-shark-100 text-pink-600 " +
            "dark:bg-shark-900 dark:text-pink-500 " +
            "hover:text-gray-600 hover:bg-shark-200 dark:hover:bg-shark-850 dark:hover:text-white " +
            "disabled:bg-shark-100 disabled:text-pink-600 dark:disabled:bg-shark-850 dark:disabled:text-pink-500",
        },
      },
      tertiary: {
        variant: {
          solid:
            "font-bold bg-shark-100 text-shark-600 " +
            "dark:bg-shark-950 dark:text-white " +
            "hover:text-gray-800 dark:hover:bg-shark-850 dark:hover:text-white " +
            "disabled:bg-shark-200 disabled:text-shark-600 dark:disabled:!bg-shark-850 dark:disabled:!text-pink-500",
        },
      },
      link: {
        variant: {
          link:
            "underline underline-offset-4 " +
            "text-pink-600 hover:text-pink-800 " +
            "dark:text-pink-500 dark:hover:text-pink-600 " +
            "hover:underline focus-visible:ring-0 " +
            "disabled:text-shark-400 dark:disabled:text-shark-700",
        },
      },
      ghost: {
        variant: {
          ghost:
            "text-shark-900 bg-transparent hover:bg-shark-200 " +
            "dark:text-white dark:hover:bg-shark-900 " +
            "focus-visible:ring-0 " +
            "disabled:text-shark-400 dark:disabled:text-shark-700",
        },
      },
      outline: {
        variant: {
          outline:
            "ring-1 ring-inset ring-current ring-shark-200 text-shark-900 " +
            "hover:text-white hover:ring-0 hover:bg-pink-600 " +
            "dark:ring-shark-900 dark:hover:ring-0 dark:text-shark-600 dark:hover:text-white dark:hover:bg-pink-500 " +
            "disabled:text-shark-200 dark:disabled:text-shark-600",
        },
      },
      icon: {
        variant: {
          outline:
            "ring-1 ring-inset ring-current ring-shark-200" +
            "text-shark-900 hover:text-white hover:ring-0 hover:bg-pink-500 " +
            "focus-visible:ring-gray-400 dark:focus-visible:ring-pink-500 " +
            "dark:ring-shark-900 dark:hover:ring-0 dark:text-shark-600 dark:hover:text-white dark:hover:bg-pink-500 " +
            "disabled:text-shark-200 dark:disabled:text-shark-600",
        },
      },
    },
  }

  const props = defineProps({
    block: {
      type: Boolean,
      default: false,
    },
    href: {
      type: String,
      default: null,
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    padded: {
      type: Boolean,
      default: true,
    },
    shape: {
      type: String,
      default: "rounded",
    },
    size: {
      type: String,
      default: "md",
    },
    target: {
      type: String,
      default: null,
    },
    to: {
      type: [String, Object],
      default: null,
    },
    theme: {
      type: String,
      default: "primary",
    },
    truncate: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: "button",
    },
    variant: {
      type: String,
      default: "solid",
    },
  })

  const themeVariant = computed(() => {
    if (["primary", "secondary", "tertiary"].includes(props.theme)) {
      return "solid"
    } else if (["link"].includes(props.theme)) {
      return "link"
    } else if (["outline", "icon"].includes(props.theme)) {
      return "outline"
    } else if (["ghost"].includes(props.theme)) {
      return "ghost"
    }
    return "solid"
  })

  const uiStyles = computed(() => {
    const rounded =
      props.shape === "rounded"
        ? "rounded-full"
        : props.shape === "square"
        ? "rounded-md"
        : "rounded-lg"

    const base =
      "focus:outline-none focus-visible:outline-0 disabled:cursor-not-allowed disabled:opacity-1 flex-shrink-0"

    const padding = props.iconOnly
      ? {
          "2xs": "px-1 py-1",
          xs: "px-2 py-2",
          sm: "px-2.5 py-2.5",
          md: "px-3 py-3",
          lg: "px-3.5 py-3.5",
          xl: "px-4 py-4",
        }
      : {
          "2xs": "px-3 py-1",
          xs: "px-3.5 py-1.5",
          sm: "px-3.5 py-1.5",
          md: "px-6 py-2",
          lg: "px-8 py-2.5",
          xl: "px-8 py-2.5",
        }

    const size = {
      "2xs": "text-xs",
      xs: "text-xs",
      sm: "text-sm",
      md: "text-sm",
      lg: "text-sm",
      xl: "text-base",
    }

    return { ...styles.favoritely[props.theme], rounded, base, padding, size }
  })
</script>
